import { useMemo } from 'react';
import uniq from 'lodash-es/uniq';
import { PaymentMethodId } from '../bff-api/baskets/model/PaymentMethodId';
import { useBasket } from '../bff-api/baskets/useBasket';
import { useProducts } from '../bff-api/products/useProducts';
import { getShipToShippingTotal } from '../bff-api/utils/basket-utils';
import { DEFAULT_CURRENCY, SHIPPING_PROTECTION_PRODUCT_ID } from '../constants';
import {
    buildShipments,
    getBackorderProductItems,
    getBackorderProductItemsDictionary,
    getBopisProductItems,
    getCulinaryProductItems,
    getDropShipProductItems,
    getDropShipProductItemsDictionary,
    getEGiftCardProductItems,
    getExperienceProductItems,
    getGiftRegistryProductItems,
    getLastProductOnBasket,
    getProductItemsCounts,
    getSameDayDeliveryProductItems,
    getShippableProductItems,
    hasOfflineProducts,
    isMixedBagWithOneAddress,
    isPhysicalGiftCard,
    isShippingProtection,
} from '../utils/product-utils';
import { useBasketBopisStore } from './useBopisStore';
import { useSDDStore } from './useSDDStore';
import type { CartContext } from '../contexts/model/CartContext';

export const useCart = (): CartContext => {
    const { isLoading, data: basket } = useBasket();
    const goLocalStoreQuery = useSDDStore();
    const isGoLocalStoreLoading = goLocalStoreQuery.isLoading && !!basket?.c_goLocalStoreID;
    const bopisStore = useBasketBopisStore().data;
    // Filter out Safonia (shipping protection) - it's a virtual service product that doesn't need product details fetched
    const { data: products = [] } = useProducts(
        basket?.productItems
            ?.map((pi) => pi.productId)
            .filter((id) => id !== SHIPPING_PROTECTION_PRODUCT_ID),
    );

    const basketIndicators = useMemo(() => {
        const productItems = basket?.productItems ?? [];

        let shippingProducts = getShippableProductItems(productItems).sort((a, b) => {
            // Desired order:
            // 0: Non gift registry shippable
            // 1: Gift registry shippable
            // 2: Shipping protection
            // 3: Physical gift card
            const isGiftRegistry = (p: typeof a): boolean => Boolean(p.c_GiftRegistryLineItem);
            const rank = (p: typeof a): number => {
                if (isPhysicalGiftCard(p)) return 3;
                if (isShippingProtection(p)) return 2;
                return isGiftRegistry(p) ? 1 : 0;
            };

            return rank(a) - rank(b);
        });
        const dropShipProducts = getDropShipProductItems(productItems);
        let dropShipProductsDictionary = getDropShipProductItemsDictionary(dropShipProducts);
        const backorderProducts = getBackorderProductItems(productItems);
        const backorderProductsDictionary = getBackorderProductItemsDictionary(backorderProducts);
        const sameDayDeliveryProducts = getSameDayDeliveryProductItems(productItems);

        // Filter out backorder items from shipping products for regular shipment
        const regularShippingProducts = shippingProducts.filter(
            ({ c_onBackorder }) => !c_onBackorder,
        );

        // If regular shipment contains only Safonia (shipping protection) items and there are dropship items,
        // move Safonia to the first dropship shipment to avoid confusing EDD display.
        // Note: We check for any number of Safonia items (though typically only one exists per cart
        // based on current business rules, as shipping protection is added once per order).
        const hasOnlySafoniaInRegularShipment =
            regularShippingProducts.length >= 1 &&
            regularShippingProducts.every(isShippingProtection);

        if (hasOnlySafoniaInRegularShipment && Object.keys(dropShipProductsDictionary).length > 0) {
            // Add Safonia items to the end of the first dropship dictionary entry
            const dropshipKeys = Object.keys(dropShipProductsDictionary).sort();
            const firstKey = dropshipKeys[0];
            dropShipProductsDictionary = {
                ...dropShipProductsDictionary,
                [firstKey]: [...dropShipProductsDictionary[firstKey], ...regularShippingProducts],
            };
            // Remove Safonia from shippingProducts, keeping only backorder items
            // Since regularShippingProducts contains only Safonia items at this point,
            // we just need to filter shippingProducts to keep backorder items
            shippingProducts = shippingProducts.filter(({ c_onBackorder }) => c_onBackorder);
        }

        const counts = getProductItemsCounts(productItems);

        const hasMultipleGiftRegistries =
            uniq(
                productItems?.map((pi) => pi.productListItem?.productList?.c_id).filter(Boolean) ??
                    [],
            ).length > 1;

        return {
            ...counts,

            currency: basket?.currency ?? DEFAULT_CURRENCY,
            giftCardPayments:
                basket?.paymentInstruments?.filter(
                    (payment) => payment.paymentMethodId === PaymentMethodId.GIFT_CARD,
                ) ?? [],
            defaultShipment: basket?.shipments?.find((shipment) => shipment.shipmentId === 'me'),
            shippingProducts,
            sameDayDeliveryProducts,
            bopisProducts: getBopisProductItems(productItems),
            dropShipProducts,
            dropShipProductsDictionary,
            backorderProducts,
            backorderProductsDictionary,
            experienceProducts: getExperienceProductItems(productItems),
            giftRegistryProducts: getGiftRegistryProductItems(productItems),
            shipments: buildShipments(
                shippingProducts,
                sameDayDeliveryProducts,
                dropShipProductsDictionary,
                backorderProductsDictionary,
            ),
            culinaryProducts: getCulinaryProductItems(productItems),
            eGiftCardProducts: getEGiftCardProductItems(productItems),
            hasProductItems: productItems?.length > 0,
            hasBopisProducts: counts.countBopis > 0,
            hasDropShipProducts: counts.countDropShip > 0,
            hasGiftRegistryProducts: counts.countGiftRegistry > 0,
            hasOnlyBackorderProducts:
                counts.countBackorder > 0 && counts.countBackorder === counts.countShippable,
            hasOnlyDropShipProducts:
                counts.countDropShip > 0 && counts.countDropShip === counts.countShippable,
            hasOnlyGiftCardProducts:
                counts.countGiftCard > 0 &&
                !!productItems.length &&
                counts.countGiftCard === productItems.length,
            hasShippingProducts: counts.countShippable > 0,
            hasNonSameDayShippingProducts:
                counts.countShippable > 0 && counts.countShippable > counts.countSameDayDelivery,
            hasNonGiftRegistryShippingProducts: counts.countShippable > counts.countGiftRegistry,
            hasShippingBySLTProducts: counts.countShippableBySLT > 0,
            hasSameDayDeliveryProducts: counts.countSameDayDelivery > 0,
            hasMultipleGiftRegistries,
            isMixedBagWithOneAddress: isMixedBagWithOneAddress(productItems),
            hasOfflineProducts: hasOfflineProducts(products),
            shipToShippingTotal: getShipToShippingTotal(basket),
            lastProductAddedToBasket: getLastProductOnBasket(productItems, products),
        };
    }, [basket, products]);

    const cartState = useMemo<CartContext>(
        () => ({
            basket,
            ...basketIndicators,
            store: bopisStore,
            goLocalStore: goLocalStoreQuery.data,

            get isEmpty(): boolean {
                return !isLoading && (basket?.productItems?.length ?? 0) === 0;
            },

            get isLoading(): boolean {
                return isLoading || isGoLocalStoreLoading;
            },
        }),
        [
            basket,
            bopisStore,
            goLocalStoreQuery.data,
            basketIndicators,
            isLoading,
            isGoLocalStoreLoading,
        ],
    );

    return cartState;
};
